import { useEffect, useMemo, useState } from "react";
import { ReactComponent as CloseSvg } from "../../assets/icons/close.svg";
import {
  ModalContainer,
  CloseButton,
  ModalContent,
  ModalTitle,
  Row,
  ContentContainer,
  Content,
  EmptyState,
  Column,
  ModalFooter,
  SubmitButton,
  ButtonsContainer,
  CancelButton,
  Tabs,
  Tab,
  UploadCard2,
  UploadText,
  UploadDescriptionText,
  Line,
  ContentWrapper,
  AnonymizedCard,
  AnonymizedCardHeader,
  AnonymizedCardTitle,
  RefreshIcon,
  AnonymizedCardText,
} from "./GalleryModal.styles";
import Modal from "../utils/Modal/Modal";
import { useLockBodyScroll } from "../../hooks/useLockBodyScroll";
import { useDispatch, useSelector } from "react-redux";
import {
  selectGalleryData,
  selectGalleryDataPending,
  selectGetAnonymizedCollectionPending,
  selectGetAnonymizedCollections,
} from "../../store/slices/model/slice";
import {
  fetchAnonymizedCollections,
  fetchDefaultGallery,
} from "../../store/slices/model/asyncThunks";
import { useMediaQuery } from "react-responsive";
import GalleryImage from "./GalleryImage";
import { toast } from "../modern-ui/sonner";
import { ReactComponent as ImagesSvg } from "../../assets/icons/imageUserPlus.svg";
import generatingAnimation from "../../assets/animations/onLoad.json";
import LottieIcon from "../../components/LottieIcon/LottieIcon";
import GalleryModalSkeletonLoader from "../utils/GalleryModalSkeletonLoader";
import { ReactComponent as RefreshSvg } from "../../assets/icons/refresh.svg";

const GalleryModal = ({
  isOpen,
  onClose,
  onConfirm,
  selectedImages,
  uploadedImagesCount,
  setOpenAnonymizeModal,
}) => {
  const [selectedTab, setSelectedTab] = useState("public");

  useLockBodyScroll(isOpen);

  const isDesktop = useMediaQuery({ maxWidth: 1224 });
  const isTable = useMediaQuery({ maxWidth: 950 });
  const isMobile = useMediaQuery({ maxWidth: 768 });

  const numOfColumns = useMemo(() => {
    switch (true) {
      case isMobile:
        return 2;
      case isTable:
        return 3;
      case isDesktop:
        return 3;
      default:
        return 3;
    }
  }, [isDesktop, isTable, isMobile]);

  const dispatch = useDispatch();
  const [addedImages, setAddedImages] = useState(selectedImages);

  const data = useSelector(selectGalleryData);

  const anonymizedCollections = useSelector(selectGetAnonymizedCollections);
  const anonymizedCollectionsPending = useSelector(
    selectGetAnonymizedCollectionPending,
  );

  const loading = useSelector(selectGalleryDataPending);

  useEffect(() => {
    setAddedImages(selectedImages);
  }, [selectedImages]);

  useEffect(() => {
    dispatch(fetchDefaultGallery());
  }, [dispatch]);

  useEffect(() => {
    if (isOpen) {
      dispatch(fetchAnonymizedCollections());
    }
  }, [dispatch, isOpen]);

  const handleCancel = () => {
    setSelectedTab("public");
    onClose();
  };

  const handleRefresh = () => {
    dispatch(fetchAnonymizedCollections());
  };

  const selectedCountText = isMobile
    ? ""
    : `(${uploadedImagesCount + addedImages.length}/3-6)`;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      customOverlayStyles={{ zIndex: 3100 }}
    >
      <ModalContainer>
        <ModalContent>
          <Row>
            <ModalTitle>{`Select Testing Image (${
              uploadedImagesCount + addedImages.length
            }/3-6)`}</ModalTitle>
            <CloseButton onClick={handleCancel}>
              <CloseSvg width={20} height={20} />
            </CloseButton>
          </Row>

          <Tabs $activeTab={selectedTab}>
            <Tab
              $active={selectedTab === "public"}
              onClick={() => {
                setSelectedTab("public");
              }}
            >
              Public Images
            </Tab>
            <Tab
              $active={selectedTab === "my"}
              onClick={() => {
                setSelectedTab("my");
              }}
            >
              My Images
            </Tab>
          </Tabs>
          {selectedTab === "public" ? (
            <ContentContainer>
              {loading || !data ? (
                <GalleryModalSkeletonLoader
                  numOfColumns={numOfColumns}
                  rowsPerColumn={5}
                />
              ) : (
                <Content>
                  {!data.length ? (
                    <EmptyState>
                      <h3>No videos found</h3>
                      <p>There are no videos to display at the moment.</p>
                    </EmptyState>
                  ) : (
                    Array.from({ length: numOfColumns }).map((_, colIndex) => {
                      return (
                        <Column key={colIndex} $numOfColumns={numOfColumns}>
                          {data
                            .filter((_, i) => i % numOfColumns === colIndex)
                            .map((url, i) => {
                              const baseUrl = url.split("?")[0];

                              return (
                                <GalleryImage
                                  key={i + url}
                                  url={url}
                                  isAdded={addedImages.some(
                                    (u) => u.split("?")[0] === baseUrl,
                                  )}
                                  onToggle={() => {
                                    setAddedImages((prev) => {
                                      const exists = prev.some(
                                        (u) => u.split("?")[0] === baseUrl,
                                      );
                                      if (exists) {
                                        return prev.filter(
                                          (u) => u.split("?")[0] !== baseUrl,
                                        );
                                      } else {
                                        return [...prev, url];
                                      }
                                    });
                                  }}
                                />
                              );
                            })}
                        </Column>
                      );
                    })
                  )}
                </Content>
              )}
            </ContentContainer>
          ) : (
            <ContentContainer>
              <ContentWrapper>
                <UploadCard2>
                  <ImagesSvg />
                  <UploadText
                    onClick={() => {
                      setOpenAnonymizeModal(true);
                    }}
                  >
                    Create New Image
                  </UploadText>

                  <UploadDescriptionText>
                    Upload images containing people and we will automatically
                    remove identifiable features while preserving the original
                    style and composition, generating privacy-protected
                    anonymized images suitable for public display.
                  </UploadDescriptionText>
                </UploadCard2>
                {!!anonymizedCollections && !!anonymizedCollections.length && (
                  <Row>
                    <ModalTitle>Images</ModalTitle>
                    <Line />
                    <RefreshIcon
                      $spinning={anonymizedCollectionsPending}
                      onClick={handleRefresh}
                    >
                      <RefreshSvg width={18} height={18} />
                    </RefreshIcon>
                  </Row>
                )}

                <Content $private>
                  {anonymizedCollectionsPending ? (
                    <GalleryModalSkeletonLoader
                      numOfColumns={numOfColumns}
                      rowsPerColumn={4}
                    />
                  ) : (
                    Array.from({ length: numOfColumns }).map((_, colIndex) => {
                      return (
                        <Column key={colIndex} $numOfColumns={numOfColumns}>
                          {anonymizedCollections
                            ?.filter((_, i) => i % numOfColumns === colIndex)
                            .map((obj) => {
                              if (obj.status === "IN_PROGRESS") {
                                const inputImg =
                                  obj?.input?.image?.resizedImage;
                                const aspectRatio = inputImg
                                  ? `${inputImg.width}/${inputImg.height}`
                                  : "3/4";
                                const baseUrl = obj.input.image.url;
                                return (
                                  <AnonymizedCard
                                    key={obj._id}
                                    style={{ aspectRatio }}
                                    $image={baseUrl}
                                  >
                                    <AnonymizedCardHeader>
                                      <LottieIcon
                                        animationData={generatingAnimation}
                                        size={82}
                                        trigger="loop"
                                        style={{
                                          marginTop: -20,
                                          marginBottom: -10,
                                          zIndex: 1,
                                          filter:
                                            "drop-shadow(0 2px 4px rgba(0, 0, 0, 0.5))",
                                        }}
                                      />

                                      <AnonymizedCardTitle>
                                        Generating...
                                      </AnonymizedCardTitle>

                                      <AnonymizedCardText>
                                        It may take 1-2 minutes
                                        <br />
                                        to generate the image.
                                      </AnonymizedCardText>
                                    </AnonymizedCardHeader>
                                  </AnonymizedCard>
                                );
                              } else {
                                const baseUrl =
                                  obj.output.image.url.split("?")[0];

                                const inputImg =
                                  obj?.input?.image?.resizedImage;
                                const aspectRatio = inputImg
                                  ? `${inputImg.width}/${inputImg.height}`
                                  : "3/4";
                                return (
                                  <GalleryImage
                                    initialAspectRatio={aspectRatio}
                                    key={obj._id}
                                    url={obj.output.image.url}
                                    isAdded={addedImages.some(
                                      (u) => u.split("?")[0] === baseUrl,
                                    )}
                                    onToggle={() => {
                                      setAddedImages((prev) => {
                                        const exists = prev.some(
                                          (u) => u.split("?")[0] === baseUrl,
                                        );
                                        if (exists) {
                                          return prev.filter(
                                            (u) => u.split("?")[0] !== baseUrl,
                                          );
                                        } else {
                                          return [
                                            ...prev,
                                            obj.output.image.url,
                                          ];
                                        }
                                      });
                                    }}
                                  />
                                );
                              }
                            })}
                        </Column>
                      );
                    })
                  )}
                </Content>
              </ContentWrapper>
            </ContentContainer>
          )}
          <ModalFooter>
            <SubmitButton
              onClick={() => {
                setAddedImages([]);
              }}
            >
              Clear All
            </SubmitButton>
            <ButtonsContainer>
              {!isMobile && (
                <CancelButton
                  onClick={() => {
                    onClose();
                  }}
                >
                  Cancel
                </CancelButton>
              )}
              <SubmitButton
                onClick={() => {
                  if (uploadedImagesCount + addedImages.length > 6) {
                    toast.error("Please select max 6 images");
                  } else {
                    onConfirm(addedImages);
                    onClose();
                  }
                }}
              >{`Confirm ${selectedCountText}`}</SubmitButton>
            </ButtonsContainer>
          </ModalFooter>
        </ModalContent>
      </ModalContainer>
    </Modal>
  );
};

export default GalleryModal;
